// components/MyPage/UserSettings/UserInfoForm2.jsx
import { useEffect, useState } from 'react';
import {
    Card, CardContent, Box, Typography, Divider,
    TextField, MenuItem, Chip, Stack, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import api from '../../../api/axios';
import { useUser } from '../../../contexts/UserContext';

/* ───────── Trait 목록 ───────── */
const traitOptions = [
    { label: '도미넌트', color: 'error' },
    { label: '서브미시브', color: 'primary' },
    { label: '마스터/미스트레스', color: 'error' },
    { label: '슬레이브', color: 'primary' },
    { label: '오너', color: 'error' },
    { label: '펫', color: 'primary' },
    { label: '대디/마미', color: 'error' },
    { label: '리틀', color: 'primary' },
    { label: '브랫 테이머', color: 'error' },
    { label: '브랫', color: 'primary' },
    { label: '사디스트', color: 'error' },
    { label: '마조히스트', color: 'primary' },
    { label: '헌터', color: 'error' },
    { label: '프레이', color: 'primary' },
    { label: '디그레이더', color: 'error' },
    { label: '디그레이디', color: 'primary' },
    { label: '스팽커', color: 'error' },
    { label: '스팽키', color: 'primary' },
    { label: '리거', color: 'error' },
    { label: '로프버니', color: 'primary' },
];

const MAX_SELECTED = 3;

export default function UserInfoForm2({ setSnack }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { refreshUserInfo } = useUser();

    const [offline, setOffline] = useState('');
    const [traits, setTraits] = useState([]);
    const [saving, setSaving] = useState(false);

    // 구인 관련
    const [recruitMain, setRecruitMain] = useState('');
    const [recruitSub, setRecruitSub] = useState('');
    const [recruitNote, setRecruitNote] = useState('');
    const [recruitFlag, setRecruitFlag] = useState('');
    const [recruitCustom, setRecruitCustom] = useState('');

    useEffect(() => {
        api.get('/api/user-info/')
            .then(({ data }) => {
                setOffline(data.offline_status || '');
                setTraits(data.detailed_traits || []);
                setRecruitMain(data.recruit_main || '');
                setRecruitSub(data.recruit_sub || '');
                setRecruitNote(data.recruit_note || '');
                setRecruitFlag(data.recruit_flag || '');
                // ✅ 추가: '구인중 + 기타'로 저장되어 있던 경우 입력창에 복원
                if (data.recruit_main === '구인중' && data.recruit_sub === '기타') {
                    setRecruitCustom((data.recruit_note || '').slice(0, 10));
                }
            })
            .catch(() =>
                setSnack({ open: true, msg: '유저 정보를 불러오지 못했습니다.', severity: 'error' })
            );
    }, [setSnack]);

    const toggleTrait = (trait) => {
        setTraits((prev) =>
            prev.includes(trait)
                ? prev.filter((t) => t !== trait)
                : prev.length >= MAX_SELECTED
                    ? (alert('최대 3개까지만 선택할 수 있습니다.'), prev)
                    : [...prev, trait]
        );
    };

    const renderGroup = (color, label) => (
        <Box sx={{ mt: 3 }}>
            <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ color: `${color}.main`, mb: 1 }}
            >
                {label}
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1, rowGap: 1 }}>
                {traitOptions
                    .filter((t) => t.color === color)
                    .map((t) => (
                        <Chip
                            key={t.label}
                            label={t.label}
                            color={traits.includes(t.label) ? color : 'default'}
                            variant={traits.includes(t.label) ? 'filled' : 'outlined'}
                            onClick={() => toggleTrait(t.label)}
                            sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                        />
                    ))}
            </Box>
            <Divider sx={{ mt: 2 }} />
        </Box>
    );

    const handleSave = () => {
        setSaving(true);

        // ✅ payload에 기타(직접작성)일 경우 recruit_note를 교체
        const payload = {
            offline_status: offline,
            detailed_traits: traits,
            recruit_main: recruitMain,
            recruit_sub: recruitSub,
            recruit_note:
                recruitMain === '구인중' && recruitSub === '기타'
                    ? recruitCustom
                    : recruitNote,
            recruit_flag: recruitFlag,
        };

        api.post('/api/accounts/user/update/', payload)
            .then(() => {
                refreshUserInfo();
                setSnack({ open: true, msg: '저장 완료!', severity: 'success' });
            })
            .catch(() =>
                setSnack({ open: true, msg: '저장 실패', severity: 'error' })
            )
            .finally(() => setSaving(false));
    };

    return (
        <Card sx={{ mb: 4 }}>
            <CardContent>
                <Typography
                    variant={isMobile ? 'h6' : 'h5'}
                    fontWeight="bold"
                    sx={{ mb: 3 }}
                >
                    🧭 성향 및 활동 설정
                </Typography>

                {/* 구인 상태 */}
                <Stack spacing={3}>
                    <TextField
                        select label="구인 상태" fullWidth
                        value={recruitMain}
                        onChange={(e) => {
                            setRecruitMain(e.target.value);
                            setRecruitSub('');
                            setRecruitNote('');
                            setRecruitCustom('');   // ✅ 추가
                        }}
                    >
                        <MenuItem value="구인중">구인중</MenuItem>
                        <MenuItem value="구인안함">구인안함</MenuItem>
                    </TextField>

                    {recruitMain === '구인중' && (
                        <>
                            <TextField
                                select
                                label="파트너 여부"
                                fullWidth
                                value={recruitSub}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setRecruitSub(v);
                                    if (v !== '기타') setRecruitCustom(''); // 기타 해제 시 초기화
                                }}
                            >
                                <MenuItem value="파트너 있음">파트너 있음</MenuItem>
                                <MenuItem value="파트너 없음">파트너 없음</MenuItem>
                                <MenuItem value="기타">기타(직접작성)</MenuItem>
                            </TextField>

                            {/* ✅ 기타 선택 시에만 10자 제한 입력창 노출 */}
                            {recruitSub === '기타' && (
                                <TextField
                                    sx={{ mt: 1 }}
                                    label="직접 입력 (10자 이내)"
                                    placeholder="예: 상황에 따라"
                                    fullWidth
                                    value={recruitCustom}
                                    onChange={(e) => setRecruitCustom(e.target.value.slice(0, 10))}
                                    inputProps={{ maxLength: 10 }}
                                    helperText={`${recruitCustom.length}/10`}
                                />
                            )}
                        </>
                    )}

                    {recruitMain === '구인안함' && (
                        <>
                            <TextField
                                select label="세부 상태" fullWidth
                                value={recruitSub}
                                onChange={(e) => {
                                    setRecruitSub(e.target.value);
                                    setRecruitNote('');
                                }}
                            >
                                <MenuItem value="생각없음">생각없음</MenuItem>
                                <MenuItem value="플파">플파</MenuItem>
                                <MenuItem value="디엣">디엣</MenuItem>
                                <MenuItem value="연디">연디</MenuItem>
                            </TextField>

                            {['플파', '디엣', '연디'].includes(recruitSub) && (
                                <TextField
                                    label={`${recruitSub} 상대 닉네임 (선택)`}
                                    fullWidth
                                    value={recruitNote}
                                    onChange={(e) => setRecruitNote(e.target.value)}
                                />
                            )}
                        </>
                    )}

                    {/* 플래그 */}
                    <TextField
                        label="관계 플래그 (예: 모노가미, 폴리)"
                        value={recruitFlag}
                        onChange={(e) => setRecruitFlag(e.target.value)}
                        fullWidth
                    />

                    {/* 오프 여부 */}
                    <TextField
                        select label="오프 여부" fullWidth
                        value={offline}
                        onChange={(e) => setOffline(e.target.value)}
                    >
                        <MenuItem value="오프(O)">오프(O)</MenuItem>
                        <MenuItem value="오프(X)">오프(X)</MenuItem>
                    </TextField>
                </Stack>

                {/* 성향 */}
                <Typography variant="subtitle2" sx={{ mt: 4, mb: 1 }}>
                    세부 성향 (최대 3개 선택)
                </Typography>
                {renderGroup('error', 'Top 계열')}
                {renderGroup('primary', 'Bottom 계열')}

                <LoadingButton
                    loading={saving}
                    onClick={handleSave}
                    variant="contained"
                    fullWidth
                    sx={{ mt: 4, py: 1.2 }}
                >
                    저장
                </LoadingButton>
            </CardContent>
        </Card>
    );
}
